import { FC, ChangeEvent, useMemo, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import { IncentiveOffer } from 'modules/incentivesMarket/types';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

import IncentiveOfferSellBreakdown from './IncentiveOfferSellBreakdown';

export type LegacyIncentiveOfferActionCardProps = {
  offer: IncentiveOffer;
  availableShares: number;
  sellerOfferCap?: {
    maxUnitsSellerCanOffer: number;
    effectiveMax: number;
  };
  sellerPlans: IncentivesMarketPlanContext[];
  purchaseQuantity: number;
  isSellOffer: boolean;
  showSellBreakdown: boolean;
  hasCustomSellerLimits: boolean;
  acceptOfferDescription: string;
  mustBuyAll: boolean;
  minPurchaseQuantity: number;
  isOwnOffer: boolean;
  hasPartnerBuyer: boolean;
  /* eslint-disable-next-line no-unused-vars */
  onQuantityChange: (quantity: number) => void;
  onBuyShares: () => void;
  onViewOtherOffers: () => void;
};

const LegacyIncentiveOfferActionCard: FC<LegacyIncentiveOfferActionCardProps> = ({
  offer,
  availableShares,
  sellerOfferCap,
  sellerPlans,
  purchaseQuantity,
  isSellOffer,
  showSellBreakdown,
  hasCustomSellerLimits,
  acceptOfferDescription,
  mustBuyAll,
  minPurchaseQuantity,
  isOwnOffer,
  hasPartnerBuyer,
  onQuantityChange,
  onBuyShares,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();
  const [inputValue, setInputValue] = useState<string>('0');

  const inputError = useMemo(() => {
    if (mustBuyAll || !inputValue) return null;
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed === 0) return null;
    if (parsed < minPurchaseQuantity) {
      return t('IncentivesMarketMinUnitsPerAcceptanceError', {
        min: formatNumber(minPurchaseQuantity),
      });
    }
    if (parsed > availableShares) {
      return t('IncentivesMarketMaxUnitsExceededError', {
        max: formatNumber(availableShares),
      });
    }
    return null;
  }, [mustBuyAll, inputValue, minPurchaseQuantity, availableShares, t]);

  const parsedInputValue = parseInt(inputValue, 10);
  const isInputValid = mustBuyAll
    ? availableShares > 0 &&
      purchaseQuantity > 0 &&
      purchaseQuantity === availableShares
    : !inputError && !isNaN(parsedInputValue) && parsedInputValue > 0;

  const isBuyButtonDisabled = useMemo(
    () => isOwnOffer || !hasPartnerBuyer || !isInputValid,
    [isOwnOffer, hasPartnerBuyer, isInputValid]
  );

  const subtotal = useMemo(
    () => purchaseQuantity * offer.unitPrice,
    [purchaseQuantity, offer.unitPrice]
  );

  const total = useMemo(() => subtotal, [subtotal]);

  const quickAmounts = useMemo(() => {
    if (mustBuyAll) {
      return availableShares > 0 ? [availableShares] : [];
    }

    const amounts: number[] = [];
    if (availableShares >= 10) amounts.push(10);
    if (availableShares >= 25) amounts.push(25);
    if (availableShares > 0) amounts.push(availableShares);
    const filtered = amounts.filter((a) => a >= minPurchaseQuantity);

    const uniqueSorted = [...new Set(filtered)].sort((a, b) => a - b);

    return uniqueSorted;
  }, [availableShares, mustBuyAll, minPurchaseQuantity]);

  const handleIncrement = () => {
    if (!mustBuyAll && purchaseQuantity < availableShares) {
      const newValue = purchaseQuantity + 1;
      setInputValue(String(newValue));
      onQuantityChange(newValue);
    }
  };

  const handleDecrement = () => {
    if (!mustBuyAll && purchaseQuantity > minPurchaseQuantity) {
      const newValue = purchaseQuantity - 1;
      setInputValue(String(newValue));
      onQuantityChange(newValue);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);

    const value = parseInt(raw, 10);
    if (
      !isNaN(value) &&
      value >= minPurchaseQuantity &&
      value <= availableShares
    ) {
      onQuantityChange(value);
    }
  };

  const handleQuickAmount = (amount: number) => {
    setInputValue(String(amount));
    onQuantityChange(amount);
  };

  const showPartialMinHint =
    offer.isPartialAmountEnabled && minPurchaseQuantity > 1;

  return (
    <div
      className="card card-bordered"
      style={{ position: 'sticky', top: '20px' }}
    >
      <div className="card-inner">
        <div className="text-center mb-4">
          <em className="icon ni ni-cart fs-24px text-info" />
          <h5 className="mt-2 mb-1">
            {isSellOffer
              ? t('IncentivesMarketBuyUnits')
              : t('IncentivesMarketSellUnits')}
          </h5>
          <p className="text-soft fs-13px mb-0">{acceptOfferDescription}</p>
        </div>

        <hr />

        <div
          className={`form-group mb-3 ${
            isOwnOffer ? 'is-disable cursor-not-allowed' : ''
          }`}
        >
          <label
            className="form-label d-flex align-items-center"
            htmlFor="purchase-quantity"
          >
            {t('IncentivesMarketHowManyUnits')}
            {showSellBreakdown && (
              <IconTooltip
                id="incentive-offer-sell-units-fifo-tooltip"
                tooltip={t('IncentivesMarketSellUnitsFifoTooltip')}
                place="top"
                className="text-soft"
                wrapperClassName="d-inline"
              />
            )}
          </label>

          <div className="d-flex w-100 mb-2" style={{ gap: '6px' }}>
            {quickAmounts.map((amount) => (
              <Button
                key={amount}
                variant={purchaseQuantity === amount ? 'primary' : 'light'}
                onClick={() => handleQuickAmount(amount)}
                disabled={isOwnOffer || mustBuyAll}
                dimStyle={!!(purchaseQuantity === amount)}
                className="flex-fill"
              >
                <span className="text-center">
                  {amount === availableShares
                    ? `${t('All')} (${formatNumber(amount)})`
                    : formatNumber(amount)}
                </span>
              </Button>
            ))}
          </div>

          <div className="input-group">
            <Button
              disabled={
                isOwnOffer ||
                mustBuyAll ||
                purchaseQuantity <= minPurchaseQuantity
              }
              onClick={handleDecrement}
              outlineStyle
              variant={ButtonVariants.SECONDARY}
            >
              −
            </Button>
            <input
              id="purchase-quantity"
              type="number"
              className={`form-control text-center${
                inputError ? ' is-invalid' : ''
              }`}
              value={mustBuyAll ? availableShares : inputValue}
              onChange={handleInputChange}
              disabled={isOwnOffer}
              readOnly={mustBuyAll}
              min={minPurchaseQuantity}
              max={availableShares}
            />
            <Button
              disabled={
                isOwnOffer || mustBuyAll || purchaseQuantity >= availableShares
              }
              onClick={handleIncrement}
              outlineStyle
              variant={ButtonVariants.SECONDARY}
            >
              +
            </Button>
          </div>

          {mustBuyAll && (
            <span className="form-note mt-1 d-block text-warning">
              {isSellOffer
                ? t('IncentivesMarketMustBuyAllUnitsNote')
                : t('IncentivesMarketMustSellAllUnitsNote')}
            </span>
          )}

          {showPartialMinHint && (
            <span
              className={`form-note mt-1 d-block ${
                inputError ? 'text-danger' : ''
              }`}
            >
              {t('IncentivesMarketMinUnitsPerAcceptanceNote')}{' '}
              <strong>{formatNumber(minPurchaseQuantity)}</strong>
            </span>
          )}

          {hasCustomSellerLimits && sellerOfferCap && !isOwnOffer && (
            <span className="form-note mt-1 d-block text-info">
              {t('IncentivesMarketMaxOfferableInOffer', {
                max: formatNumber(sellerOfferCap.maxUnitsSellerCanOffer),
              })}
            </span>
          )}
        </div>

        <hr />

        <div className="mb-4">
          {showSellBreakdown ? (
            <IncentiveOfferSellBreakdown
              sellerPlans={sellerPlans}
              purchaseQuantity={purchaseQuantity}
              unitPrice={offer.unitPrice}
              retentionPercent={0}
            />
          ) : (
            <>
              <h6 className="overline-title text-soft mb-3">
                {t('PurchaseSummary')}
              </h6>

              <div className="d-flex justify-content-between py-2">
                <span className="text-soft">{t('IncentivesMarketUnits')}</span>
                <strong>{formatNumber(purchaseQuantity)}</strong>
              </div>
              <div className="d-flex justify-content-between py-2">
                <span className="text-soft">
                  {t('IncentivesMarketPricePerUnit')}
                </span>
                <strong>{formatCurrencyDecimals(offer.unitPrice)}</strong>
              </div>
              <div className="d-flex justify-content-between py-2 border-top">
                <span className="text-soft">{t('Subtotal')}</span>
                <strong>{formatCurrencyDecimals(subtotal)}</strong>
              </div>
              <div className="d-flex justify-content-between py-3">
                <span className="fs-16px">{t('TotalToPay')}</span>
                <strong className="fs-20px text-info">
                  {formatCurrencyDecimals(total)}
                </strong>
              </div>
            </>
          )}
        </div>

        {!showSellBreakdown && (
          <div className="alert alert-light mb-4">
            <h6 className="alert-heading fs-13px">{t('AfterPurchase')}:</h6>
            <p className="mb-0 fs-13px">
              +{formatNumber(purchaseQuantity)}{' '}
              {t('IncentivesMarketUnits').toLowerCase()}
            </p>
          </div>
        )}

        <Button
          className="btn btn-lg btn-info btn-block mb-3"
          disabled={isBuyButtonDisabled}
          variant={ButtonVariants.INFO}
          onClick={onBuyShares}
        >
          <em className="icon ni ni-check-circle mr-2" />
          {isSellOffer
            ? `${t('Buy')} ${formatNumber(purchaseQuantity)} ${t(
                'IncentivesMarketUnits'
              ).toLowerCase()}`
            : `${t('Sell')} ${formatNumber(purchaseQuantity)} ${t(
                'IncentivesMarketUnits'
              ).toLowerCase()}`}
        </Button>

        <div className="text-center">
          <Button
            className="btn btn-link text-soft fs-13px"
            onClick={onViewOtherOffers}
            variant={ButtonVariants.LINK}
          >
            <em className="icon ni ni-arrow-left mr-1" />
            {t('ViewOtherOffers')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LegacyIncentiveOfferActionCard;
